import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Empty,
  Popconfirm,
  Space,
  Spin,
  Table,
  Tag,
} from "antd";
import { CreditCardOutlined, StopOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import ContentLayout from "../components/layout/ContentLayout";
import MyBreadcrumb from "../components/layout/MyBreadcrumb";
import {
  cancelSubscription,
  createSubscriptionPortalSession,
  getBillingStatus,
  getSubscriptionInvoices,
} from "../actions/subscription_actions";
import { membershipPromo } from "../utils/membershipPromo";

const statusColors = {
  active: "green",
  inactive: "gold",
  past_due: "red",
  cancelling: "orange",
  cancelled: "default",
  overdue: "red",
};

const normalizeStatus = (status) => {
  const statuses = {
    PENDING_ACTIVATION: "inactive",
    ACTIVE: "active",
    PAST_DUE: "past_due",
    CANCELLING: "cancelling",
    CANCELED: "cancelled",
    CANCELLED: "cancelled",
    UNPAID: "overdue",
  };
  return statuses[String(status || "").toUpperCase()] || String(status || "").toLowerCase();
};

const formatDate = (value) => {
  if (!value) return "-";
  return new Intl.DateTimeFormat("en", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(new Date(value));
};

const formatMajorMoney = (amount, currency = "USD") => {
  if (amount == null) return "-";
  return new Intl.NumberFormat("en", {
    style: "currency",
    currency,
  }).format(Number(amount));
};

const formatMinorMoney = (amount, currency = "USD") => {
  if (amount == null) return "-";
  return new Intl.NumberFormat("en", {
    style: "currency",
    currency,
  }).format(Number(amount) / 100);
};

const formatPaymentMethod = (billingStatus) => {
  if (billingStatus.paymentMethodBrand) {
    return `${billingStatus.paymentMethodBrand} **** ${billingStatus.paymentMethodLast4 || ""}`;
  }
  const provider = billingStatus.paymentMethod || billingStatus.paymentGateway;
  return provider
    ? String(provider).charAt(0).toUpperCase() + String(provider).slice(1)
    : "Not available";
};

export default function SettingsMembership() {
  const { message } = AntdApp.useApp();
  const queryClient = useQueryClient();
  const accountUrn = useSelector((state) => state.account.urn);
  const accountName = useSelector((state) => state.account.name);

  const {
    data: billingStatus,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["billing-status", accountUrn],
    queryFn: () => getBillingStatus(accountUrn),
    enabled: !!accountUrn,
  });

  const subscriptionId = billingStatus?.id;
  const promo = membershipPromo(billingStatus);
  const subscriptionStatus = normalizeStatus(billingStatus?.status);
  const isPendingActivation = subscriptionStatus === "inactive";
  const cancellationUnavailable =
    !subscriptionId || ["cancelled", "cancelling"].includes(subscriptionStatus);
  const canManageStripePaymentMethod =
    billingStatus?.canManagePaymentMethod === true ||
    (billingStatus?.canManagePaymentMethod == null &&
      billingStatus?.paymentGateway === "stripe" &&
      !!billingStatus?.stripeSubscriptionId &&
      !!billingStatus?.stripeCustomerId &&
      billingStatus?.simulated !== true);

  const { data: invoices = [], isLoading: invoicesLoading } = useQuery({
    queryKey: ["subscription-invoices", subscriptionId],
    queryFn: () => getSubscriptionInvoices(subscriptionId, accountUrn),
    enabled: !!subscriptionId,
  });

  const portalMutation = useMutation({
    mutationFn: () => createSubscriptionPortalSession(accountUrn),
    retry: false,
    onSuccess: (data) => {
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      message.error("Stripe portal URL was not returned.");
    },
    onError: (err) => {
      message.error(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Could not open billing portal.",
      );
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => cancelSubscription(subscriptionId, accountUrn),
    retry: false,
    onSuccess: (result) => {
      message.success(
        result?.message ||
          (isPendingActivation
            ? "Membership canceled."
            : "Cancellation scheduled at period end."),
      );
      queryClient.invalidateQueries({ queryKey: ["billing-status", accountUrn] });
      queryClient.invalidateQueries({
        queryKey: ["subscription-invoices", subscriptionId],
      });
    },
    onError: (err) => {
      message.error(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Could not cancel subscription.",
      );
    },
  });

  const invoiceColumns = [
    {
      title: "Invoice",
      dataIndex: "stripe_invoice_id",
      render: (value) => value || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) => (
        <Tag color={status === "paid" ? "green" : status === "pending" ? "gold" : "red"}>
          {(status || "unknown").toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Billing date",
      dataIndex: "billing_date",
      render: formatDate,
    },
    {
      title: "Amount",
      render: (_, row) => formatMinorMoney(row.amount, row.currency),
    },
    {
      title: "Files",
      render: (_, row) => (
        <Space>
          {row.stripe_invoice_url && (
            <a href={row.stripe_invoice_url} target="_blank" rel="noreferrer">
              View
            </a>
          )}
          {row.invoice_pdf_url && (
            <a href={row.invoice_pdf_url} target="_blank" rel="noreferrer">
              PDF
            </a>
          )}
          {!row.stripe_invoice_url && !row.invoice_pdf_url && "-"}
        </Space>
      ),
    },
  ];

  return (
    <ContentLayout
      header={
        <>
          <h1>Your membership</h1>
          <MyBreadcrumb />
        </>
      }
      bodyClassName="membership-page"
    >
      {!accountUrn ? (
        <Alert
          type="warning"
          message="No account selected"
          description="Choose an account from the sidebar before opening membership settings."
        />
      ) : isLoading ? (
        <div className="membership-loading">
          <Spin size="large" />
        </div>
      ) : isError ? (
        <Alert
          type="error"
          message="Failed to load membership"
          description={
            error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.message
          }
        />
      ) : !billingStatus ? (
        <Empty description="No billing record found for this account yet." />
      ) : (
        <>
          <Card className="membership-card" bordered={false}>
            <div className="membership-card-header">
              <div>
                <p className="membership-eyebrow">{accountName}</p>
                <h2>{billingStatus.planName || "Membership"}</h2>
                <Tag color={statusColors[subscriptionStatus] || "default"}>
                  {(subscriptionStatus || "unknown").replace("_", " ")}
                </Tag>
              </div>
              <Space wrap>
                {canManageStripePaymentMethod && (
                  <Button
                    type="primary"
                    icon={<CreditCardOutlined />}
                    onClick={() => portalMutation.mutate()}
                    loading={portalMutation.isPending}
                  >
                    Manage payment method
                  </Button>
                )}
                {isPendingActivation ? (
                  // The portal refuses this cancel (409): operations cancels and refunds unapproved signups.
                  <span className="membership-hint">
                    To cancel before your membership is approved, contact support.
                  </span>
                ) : (
                  <Popconfirm
                    title="Cancel subscription?"
                    description="Access remains active until the current paid period ends."
                    okText="Schedule cancellation"
                    cancelText="Keep subscription"
                    onConfirm={() => cancelMutation.mutate()}
                    disabled={cancellationUnavailable}
                  >
                    <Button
                      danger
                      icon={<StopOutlined />}
                      disabled={cancellationUnavailable}
                      loading={cancelMutation.isPending}
                    >
                      Cancel at period end
                    </Button>
                  </Popconfirm>
                )}
              </Space>
            </div>

            <div className="membership-metrics">
              <div>
                <span>Billing period</span>
                <strong>{billingStatus.period || "-"}</strong>
              </div>
              <div>
                <span>Recurring amount</span>
                <strong>
                  {formatMajorMoney(billingStatus.amount, billingStatus.currency)}
                </strong>
              </div>
              <div>
                <span>Next billing</span>
                <strong>
                  {formatDate(billingStatus.nextBillingDate || billingStatus.next_billing_date)}
                </strong>
              </div>
              <div>
                <span>Payment method</span>
                <strong>{formatPaymentMethod(billingStatus)}</strong>
              </div>
            </div>

            {promo && (
              <Alert
                className="membership-promo"
                type={promo.finished ? "info" : "success"}
                showIcon
                message={`Promo ${promo.code}: ${promo.percentOff}% off your package`}
                description={
                  promo.finished
                    ? `All ${promo.total} discounted payments have been used; payments are now at the full recurring amount.`
                    : `${promo.used} of ${promo.total} discounted payments used.${
                        promo.nextPayment != null
                          ? ` Your next payment is about ${formatMajorMoney(promo.nextPayment, billingStatus.currency)} instead of ${formatMajorMoney(billingStatus.amount, billingStatus.currency)}.`
                          : ""
                      }`
                }
              />
            )}

            {subscriptionStatus === "cancelling" && billingStatus.cancelsAt && (
              <Alert
                type="warning"
                showIcon
                message={`Cancellation scheduled for ${formatDate(
                  billingStatus.cancelsAt,
                )}`}
              />
            )}
            {subscriptionStatus === "cancelled" && (
              <Alert type="info" showIcon message="This membership is canceled." />
            )}
          </Card>

          <Card title="Invoices" bordered={false}>
            <Table
              columns={invoiceColumns}
              dataSource={invoices}
              loading={invoicesLoading}
              rowKey={(row) => row.id || row.stripe_invoice_id}
              pagination={{ pageSize: 8 }}
              scroll={{ x: 780 }}
            />
          </Card>
        </>
      )}
    </ContentLayout>
  );
}
